import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import {
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Scissors,
  Trash2,
  Plus,
  ZoomIn,
  ZoomOut,
  Diamond,
  Type,
  Eye,
} from "lucide-react";

const MIN_SCENE_DURATION = 0.1;
const RULER_HEIGHT = 24;
const TRACK_HEIGHT = 56;
const OVERLAY_TRACK_HEIGHT = 40;
const HANDLE_WIDTH = 8;

const OVERLAY_COLORS = {
  super: { bg: "bg-violet-500/25", border: "border-violet-400/40", text: "text-violet-200/70" },
  disclaimer: { bg: "bg-amber-500/20", border: "border-amber-400/35", text: "text-amber-200/60" },
  end_frame: { bg: "bg-emerald-500/20", border: "border-emerald-400/35", text: "text-emerald-200/60" },
  default: { bg: "bg-purple-500/20", border: "border-purple-400/30", text: "text-purple-200/60" },
};

const TimelineTrack = ({
  scenes,
  duration,
  currentTime,
  currentScene,
  playing,
  onTogglePlay,
  onSeek,
  onSceneSelect,
  onSplit,
  onDelete,
  onTrim,
  onAddScene,
  onAddKeyframe,
  sceneKeyframes,
  overlays,
  selectedOverlay,
  onOverlaySelect,
  onOverlayMove,
  onOverlayTrim,
  onOverlayDelete,
  onAddOverlay,
  onPreview,
}) => {
  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [containerWidth, setContainerWidth] = useState(800);

  // Refs for stable access in drag handlers
  const scenesRef = useRef(scenes);
  const ppsRef = useRef(50);
  const durationRef = useRef(duration);
  const onTrimRef = useRef(onTrim);

  useEffect(() => { scenesRef.current = scenes; }, [scenes]);
  useEffect(() => { durationRef.current = duration; }, [duration]);
  useEffect(() => { onTrimRef.current = onTrim; }, [onTrim]);

  // Measure container
  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Pixels per second
  const pps = useMemo(() => {
    if (!duration || duration === 0) return 50;
    return (containerWidth * zoom) / duration;
  }, [containerWidth, zoom, duration]);

  useEffect(() => { ppsRef.current = pps; }, [pps]);

  const timelineWidth = duration * pps;

  // Auto-scroll playhead into view during playback
  useEffect(() => {
    if (!trackRef.current || !playing) return;
    const playheadX = currentTime * pps;
    const scrollLeft = trackRef.current.scrollLeft;
    const visibleWidth = containerWidth;
    if (playheadX < scrollLeft || playheadX > scrollLeft + visibleWidth - 50) {
      trackRef.current.scrollLeft = playheadX - visibleWidth * 0.3;
    }
  }, [currentTime, pps, playing, containerWidth]);

  // Click on track to seek
  const handleTrackClick = useCallback(
    (e) => {
      if (isDragging) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const scrollLeft = trackRef.current?.scrollLeft || 0;
      const x = e.clientX - rect.left + scrollLeft;
      const time = Math.max(0, Math.min(duration, x / pps));
      onSeek(time);
    },
    [pps, duration, onSeek, isDragging]
  );

  // Trim drag — uses refs to avoid stale closures
  const handleTrimStart = useCallback((e, sceneIndex, edge) => {
    e.stopPropagation();
    e.preventDefault();
    const scene = scenesRef.current[sceneIndex];
    const startX = e.clientX;
    const originalTime = edge === "left" ? scene.start_time : scene.end_time;

    const calcNewTime = (clientX) => {
      const dx = clientX - startX;
      const dt = dx / ppsRef.current;
      let newTime = originalTime + dt;
      const currentScenes = scenesRef.current;
      const s = currentScenes[sceneIndex];

      if (edge === "left") {
        newTime = Math.max(0, Math.min(s.end_time - MIN_SCENE_DURATION, newTime));
        if (sceneIndex > 0)
          newTime = Math.max(currentScenes[sceneIndex - 1].end_time, newTime);
      } else {
        newTime = Math.max(
          s.start_time + MIN_SCENE_DURATION,
          Math.min(durationRef.current, newTime)
        );
        if (sceneIndex < currentScenes.length - 1)
          newTime = Math.min(currentScenes[sceneIndex + 1].start_time, newTime);
      }
      return newTime;
    };

    const handleMouseMove = (ev) => {
      const newTime = calcNewTime(ev.clientX);
      onTrimRef.current(
        sceneIndex,
        edge === "left" ? "start_time" : "end_time",
        newTime,
        false
      );
    };

    const handleMouseUp = (ev) => {
      const newTime = calcNewTime(ev.clientX);
      onTrimRef.current(
        sceneIndex,
        edge === "left" ? "start_time" : "end_time",
        newTime,
        true
      );
      setIsDragging(false);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    setIsDragging(true);
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }, []);

  // ─── Overlay drag (move) handler ───
  const onOverlayMoveRef = useRef(onOverlayMove);
  const onOverlayTrimRef = useRef(onOverlayTrim);
  useEffect(() => { onOverlayMoveRef.current = onOverlayMove; }, [onOverlayMove]);
  useEffect(() => { onOverlayTrimRef.current = onOverlayTrim; }, [onOverlayTrim]);

  const handleOverlayDragStart = useCallback((e, overlayId, action) => {
    e.stopPropagation();
    e.preventDefault();
    const overlay = (overlays || []).find((o) => o.id === overlayId);
    if (!overlay) return;
    const startX = e.clientX;
    const origStart = overlay.start_time;
    const origEnd = overlay.end_time;
    const dur = origEnd - origStart;

    const handleMove = (ev) => {
      const dx = ev.clientX - startX;
      const dt = dx / ppsRef.current;
      if (action === "move") {
        let newStart = Math.max(0, origStart + dt);
        let newEnd = newStart + dur;
        if (newEnd > durationRef.current) {
          newEnd = durationRef.current;
          newStart = newEnd - dur;
        }
        onOverlayMoveRef.current?.(overlayId, parseFloat(newStart.toFixed(2)), parseFloat(newEnd.toFixed(2)), false);
      } else {
        // trim left or right
        if (action === "left") {
          let newStart = Math.max(0, Math.min(origEnd - 0.1, origStart + dt));
          onOverlayTrimRef.current?.(overlayId, "start_time", parseFloat(newStart.toFixed(2)), false);
        } else {
          let newEnd = Math.max(origStart + 0.1, Math.min(durationRef.current, origEnd + dt));
          onOverlayTrimRef.current?.(overlayId, "end_time", parseFloat(newEnd.toFixed(2)), false);
        }
      }
    };

    const handleUp = (ev) => {
      const dx = ev.clientX - startX;
      const dt = dx / ppsRef.current;
      if (action === "move") {
        let newStart = Math.max(0, origStart + dt);
        let newEnd = newStart + dur;
        if (newEnd > durationRef.current) { newEnd = durationRef.current; newStart = newEnd - dur; }
        onOverlayMoveRef.current?.(overlayId, parseFloat(newStart.toFixed(2)), parseFloat(newEnd.toFixed(2)), true);
      } else if (action === "left") {
        let newStart = Math.max(0, Math.min(origEnd - 0.1, origStart + dt));
        onOverlayTrimRef.current?.(overlayId, "start_time", parseFloat(newStart.toFixed(2)), true);
      } else {
        let newEnd = Math.max(origStart + 0.1, Math.min(durationRef.current, origEnd + dt));
        onOverlayTrimRef.current?.(overlayId, "end_time", parseFloat(newEnd.toFixed(2)), true);
      }
      setIsDragging(false);
      document.removeEventListener("mousemove", handleMove);
      document.removeEventListener("mouseup", handleUp);
    };

    setIsDragging(true);
    if (onOverlaySelect) onOverlaySelect(overlayId);
    document.addEventListener("mousemove", handleMove);
    document.addEventListener("mouseup", handleUp);
  }, [overlays, onOverlaySelect]);


  const formatTime = (t) => {
    const mins = Math.floor(t / 60);
    const secs = t % 60;
    return `${mins}:${secs < 10 ? "0" : ""}${secs.toFixed(1)}`;
  };

  // Ruler marks
  const rulerMarks = useMemo(() => {
    if (!duration) return [];
    let interval = 1;
    if (pps < 20) interval = 10;
    else if (pps < 40) interval = 5;
    else if (pps < 80) interval = 2;
    else if (pps > 200) interval = 0.5;

    const marks = [];
    for (let t = 0; t <= duration; t += interval) {
      marks.push({ time: t, x: t * pps });
    }
    return marks;
  }, [duration, pps]);

  // Can split?
  const canSplit = useMemo(() => {
    if (currentScene < 0 || currentScene >= scenes.length) return false;
    const s = scenes[currentScene];
    return (
      currentTime > s.start_time + MIN_SCENE_DURATION &&
      currentTime < s.end_time - MIN_SCENE_DURATION
    );
  }, [currentTime, currentScene, scenes]);

  return (
    <div
      className="border-t border-cyan-500/15 bg-[#010810]/90 backdrop-blur-sm mt-6 rounded-sm overflow-hidden"
      data-testid="timeline-panel"
    >
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-cyan-500/10">
        <div className="flex items-center gap-2">
          <button
            onClick={() => onSceneSelect(Math.max(0, currentScene - 1))}
            disabled={currentScene === 0}
            className="p-1.5 text-cyan-400/50 hover:text-cyan-300 disabled:opacity-20 transition-colors"
            data-testid="timeline-prev-scene"
          >
            <SkipBack className="w-4 h-4" />
          </button>
          <button
            onClick={onTogglePlay}
            className="p-2 rounded-sm bg-cyan-500/10 text-cyan-400/70 hover:text-cyan-300 hover:bg-cyan-500/20 transition-all"
            data-testid="timeline-play-btn"
          >
            {playing ? (
              <Pause className="w-4 h-4" />
            ) : (
              <Play className="w-4 h-4" />
            )}
          </button>
          <button
            onClick={() =>
              onSceneSelect(Math.min(scenes.length - 1, currentScene + 1))
            }
            disabled={currentScene >= scenes.length - 1}
            className="p-1.5 text-cyan-400/50 hover:text-cyan-300 disabled:opacity-20 transition-colors"
            data-testid="timeline-next-scene"
          >
            <SkipForward className="w-4 h-4" />
          </button>
          <span
            className="text-xs text-cyan-400/50 font-mono ml-2 min-w-[70px]"
            data-testid="timeline-timecode"
          >
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>
          {onPreview && (
            <button
              onClick={onPreview}
              className="ml-2 flex items-center gap-1.5 px-3 py-1.5 text-xs font-['Rajdhani'] tracking-wider bg-cyan-500/10 border border-cyan-500/20 text-cyan-300/70 hover:text-cyan-200 hover:bg-cyan-500/20 rounded-sm transition-all"
              data-testid="timeline-preview-btn"
            >
              <Eye className="w-3.5 h-3.5" /> PREVIEW
            </button>
          )}
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={() => canSplit && onSplit(currentScene, currentTime)}
            disabled={!canSplit}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-['Rajdhani'] tracking-wider text-cyan-400/60 hover:text-cyan-300 hover:bg-cyan-500/10 rounded-sm disabled:opacity-20 disabled:cursor-not-allowed transition-all"
            data-testid="timeline-split-btn"
            title="Split scene at playhead"
          >
            <Scissors className="w-3.5 h-3.5" /> SPLIT
          </button>
          <button
            onClick={() => scenes.length > 1 && onDelete(currentScene)}
            disabled={scenes.length <= 1}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-['Rajdhani'] tracking-wider text-red-400/50 hover:text-red-400 hover:bg-red-500/10 rounded-sm disabled:opacity-20 disabled:cursor-not-allowed transition-all"
            data-testid="timeline-delete-btn"
            title="Delete selected scene"
          >
            <Trash2 className="w-3.5 h-3.5" /> DELETE
          </button>
          <button
            onClick={onAddScene}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-['Rajdhani'] tracking-wider text-cyan-400/60 hover:text-cyan-300 hover:bg-cyan-500/10 rounded-sm transition-all"
            data-testid="timeline-add-btn"
            title="Add scene"
          >
            <Plus className="w-3.5 h-3.5" />
          </button>
          {onAddKeyframe && (
            <button
              onClick={onAddKeyframe}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-['Rajdhani'] tracking-wider text-amber-400/50 hover:text-amber-300 hover:bg-amber-500/10 rounded-sm transition-all"
              data-testid="timeline-add-keyframe-btn"
              title="Add keyframe at playhead"
            >
              <Diamond className="w-3.5 h-3.5" />
            </button>
          )}
          {onAddOverlay && (
            <button
              onClick={() => onAddOverlay("super")}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-['Rajdhani'] tracking-wider text-violet-400/50 hover:text-violet-300 hover:bg-violet-500/10 rounded-sm transition-all"
              data-testid="timeline-add-overlay-btn"
              title="Add text overlay"
            >
              <Type className="w-3.5 h-3.5" /> OVERLAY
            </button>
          )}

          <div className="w-px h-5 bg-cyan-500/10 mx-1" />

          <button
            onClick={() => setZoom((z) => Math.max(0.5, z - 0.5))}
            className="p-1.5 text-cyan-400/40 hover:text-cyan-300 transition-colors"
            data-testid="timeline-zoom-out"
          >
            <ZoomOut className="w-3.5 h-3.5" />
          </button>
          <span className="text-[10px] text-cyan-400/40 font-mono min-w-[28px] text-center">
            {zoom.toFixed(1)}x
          </span>
          <button
            onClick={() => setZoom((z) => Math.min(10, z + 0.5))}
            className="p-1.5 text-cyan-400/40 hover:text-cyan-300 transition-colors"
            data-testid="timeline-zoom-in"
          >
            <ZoomIn className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Timeline area */}
      <div ref={containerRef} className="relative">
        <div
          ref={trackRef}
          className="overflow-x-auto overflow-y-hidden"
          style={{ cursor: isDragging ? "col-resize" : "default" }}
        >
          <div
            style={{
              width: timelineWidth,
              minWidth: "100%",
              position: "relative",
            }}
          >
            {/* Ruler */}
            <div
              className="relative border-b border-cyan-500/10"
              style={{ height: RULER_HEIGHT }}
            >
              {rulerMarks.map((mark, i) => (
                <div
                  key={i}
                  className="absolute top-0 flex flex-col items-center"
                  style={{ left: mark.x }}
                >
                  <div className="w-px h-2 bg-cyan-500/20" />
                  <span className="text-[9px] text-cyan-500/30 font-mono mt-0.5 select-none whitespace-nowrap">
                    {formatTime(mark.time)}
                  </span>
                </div>
              ))}
            </div>

            {/* Track */}
            <div
              className="relative"
              style={{ height: TRACK_HEIGHT, padding: "4px 0" }}
              onClick={handleTrackClick}
              data-testid="timeline-track"
            >
              {/* Scene blocks */}
              {scenes.map((scene, i) => {
                const left = scene.start_time * pps;
                const width = Math.max(
                  4,
                  (scene.end_time - scene.start_time) * pps
                );
                const isActive = i === currentScene;

                return (
                  <div
                    key={scene.id}
                    className={`absolute top-1 group transition-colors ${
                      isActive ? "z-10" : "z-0"
                    }`}
                    style={{
                      left: left,
                      width: width,
                      height: TRACK_HEIGHT - 8,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSceneSelect(i);
                    }}
                    data-testid={`timeline-scene-${i}`}
                  >
                    {/* Scene body */}
                    <div
                      className={`h-full rounded-[3px] border transition-all overflow-hidden ${
                        isActive
                          ? "bg-cyan-500/25 border-cyan-400/50 shadow-[0_0_12px_rgba(0,200,255,0.15)]"
                          : "bg-cyan-800/20 border-cyan-600/15 hover:bg-cyan-700/25 hover:border-cyan-500/25"
                      }`}
                    >
                      <div className="px-2 py-1 flex items-center justify-between h-full select-none relative">
                        <span
                          className={`text-[10px] font-['Rajdhani'] tracking-wider truncate ${
                            isActive
                              ? "text-cyan-200/80"
                              : "text-cyan-300/40"
                          }`}
                        >
                          {width > 60 ? `SCENE ${i + 1}` : `${i + 1}`}
                        </span>
                        {width > 100 && (
                          <span className="text-[9px] font-mono text-cyan-400/30">
                            {(scene.end_time - scene.start_time).toFixed(1)}s
                          </span>
                        )}
                        {/* Keyframe diamonds */}
                        {sceneKeyframes?.[scene.id]?.map((kf, ki) => {
                          const kfX = ((kf.time - scene.start_time) / (scene.end_time - scene.start_time)) * 100;
                          return (
                            <div
                              key={ki}
                              className="absolute bottom-1 w-2 h-2 rotate-45 bg-amber-400/70 border border-amber-300/50"
                              style={{ left: `${kfX}%`, transform: `translateX(-50%) rotate(45deg)` }}
                              data-testid={`kf-marker-${i}-${ki}`}
                            />
                          );
                        })}
                      </div>
                    </div>

                    {/* Left trim handle */}
                    <div
                      className={`absolute left-0 top-0 bottom-0 cursor-col-resize flex items-center justify-center transition-opacity ${
                        isActive
                          ? "opacity-100"
                          : "opacity-0 group-hover:opacity-60"
                      }`}
                      style={{ width: HANDLE_WIDTH }}
                      onMouseDown={(e) => handleTrimStart(e, i, "left")}
                      data-testid={`trim-left-${i}`}
                    >
                      <div className="w-[3px] h-5 rounded-full bg-cyan-400/60" />
                    </div>

                    {/* Right trim handle */}
                    <div
                      className={`absolute right-0 top-0 bottom-0 cursor-col-resize flex items-center justify-center transition-opacity ${
                        isActive
                          ? "opacity-100"
                          : "opacity-0 group-hover:opacity-60"
                      }`}
                      style={{ width: HANDLE_WIDTH }}
                      onMouseDown={(e) => handleTrimStart(e, i, "right")}
                      data-testid={`trim-right-${i}`}
                    >
                      <div className="w-[3px] h-5 rounded-full bg-cyan-400/60" />
                    </div>
                  </div>
                );
              })}

              {/* Playhead — spans all tracks */}
              <div
                className="absolute top-0 z-20 pointer-events-none"
                style={{ left: currentTime * pps, height: TRACK_HEIGHT + (overlays?.length ? OVERLAY_TRACK_HEIGHT : 0) }}
                data-testid="timeline-playhead"
              >
                <div
                  className="w-3 h-2.5 -ml-1.5 bg-red-500 rounded-t-sm"
                  style={{ clipPath: "polygon(0 0, 100% 0, 50% 100%)" }}
                />
                <div className="w-px bg-red-500 mx-auto" style={{ height: TRACK_HEIGHT + (overlays?.length ? OVERLAY_TRACK_HEIGHT : 0) - 4 }} />
              </div>
            </div>

            {/* ── Overlay Track ── */}
            {overlays?.length > 0 && (
              <div
                className="relative border-t border-purple-500/10"
                style={{ height: OVERLAY_TRACK_HEIGHT }}
                onClick={handleTrackClick}
                data-testid="timeline-overlay-track"
              >
                {/* Track label */}
                <div className="absolute left-1 top-0.5 text-[8px] text-purple-400/30 font-mono z-10 pointer-events-none">
                  OVERLAYS
                </div>
                {overlays.map((ov, i) => {
                  const left = ov.start_time * pps;
                  const width = Math.max(4, (ov.end_time - ov.start_time) * pps);
                  const isSelected = selectedOverlay === ov.id;
                  const colors = OVERLAY_COLORS[ov.type] || OVERLAY_COLORS.default;

                  return (
                    <div
                      key={ov.id}
                      className={`absolute top-1 group cursor-grab active:cursor-grabbing ${isSelected ? "z-10" : "z-0"}`}
                      style={{ left, width, height: OVERLAY_TRACK_HEIGHT - 8 }}
                      data-testid={`overlay-block-${i}`}
                    >
                      {/* Overlay body */}
                      <div
                        className={`h-full rounded-[3px] border transition-all overflow-hidden ${colors.bg} ${colors.border} ${
                          isSelected ? "shadow-[0_0_8px_rgba(168,85,247,0.2)] !border-purple-300/50" : ""
                        }`}
                        onMouseDown={(e) => handleOverlayDragStart(e, ov.id, "move")}
                      >
                        <div className={`px-2 py-0.5 flex items-center gap-1 h-full select-none ${colors.text}`}>
                          <span className="text-[9px] font-['Rajdhani'] tracking-wider truncate uppercase">
                            {ov.type?.replace("_", " ")}
                          </span>
                          {width > 80 && ov.content && (
                            <span className="text-[8px] opacity-50 truncate">{ov.content}</span>
                          )}
                        </div>
                      </div>

                      {/* Left trim handle */}
                      <div
                        className={`absolute left-0 top-0 bottom-0 cursor-col-resize flex items-center justify-center transition-opacity ${
                          isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-60"
                        }`}
                        style={{ width: HANDLE_WIDTH }}
                        onMouseDown={(e) => handleOverlayDragStart(e, ov.id, "left")}
                      >
                        <div className="w-[2px] h-3 rounded-full bg-purple-300/60" />
                      </div>

                      {/* Right trim handle */}
                      <div
                        className={`absolute right-0 top-0 bottom-0 cursor-col-resize flex items-center justify-center transition-opacity ${
                          isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-60"
                        }`}
                        style={{ width: HANDLE_WIDTH }}
                        onMouseDown={(e) => handleOverlayDragStart(e, ov.id, "right")}
                      >
                        <div className="w-[2px] h-3 rounded-full bg-purple-300/60" />
                      </div>

                      {/* Delete button */}
                      {isSelected && (
                        <button
                          className="absolute -top-2 -right-2 w-4 h-4 bg-red-500/80 rounded-full flex items-center justify-center text-white text-[8px] hover:bg-red-500 z-20"
                          onClick={(e) => { e.stopPropagation(); onOverlayDelete?.(ov.id); }}
                          data-testid={`overlay-delete-${i}`}
                        >
                          x
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Track info */}
      <div className="flex items-center justify-between px-4 py-1.5 border-t border-cyan-500/10">
        <span className="text-[10px] text-cyan-500/30 font-mono">
          {scenes.length} scene{scenes.length !== 1 ? "s" : ""}
          {overlays?.length > 0 && ` / ${overlays.length} overlay${overlays.length !== 1 ? "s" : ""}`}
        </span>
        <span className="text-[10px] text-cyan-500/30 font-mono">
          Scene {currentScene + 1} / {scenes.length} selected
        </span>
      </div>
    </div>
  );
};

export default TimelineTrack;
